import { useNavigate, useLocation } from 'react-router-dom';
import 'boxicons/css/boxicons.min.css';
import React, { useState, useEffect, useContext, useRef } from 'react';
import '../styles/MenuPage.css';
import Logo from '../images/doctor_logo.png';
import PatientListTable from './ListPatient.js';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  MdDashboard,
  MdPeople,
  MdEvent,
  MdFeedback,
  MdInventory,
  MdGroup,
  MdLogout,
  MdMenu,
  MdNotifications,
  MdCalendarToday,
  MdMeetingRoom,
  MdCheckBox,
  MdCheckCircle,
  MdShield,
  MdPerson,
  MdLocalHospital,
  MdWarning,
  MdClose,
  MdChevronLeft,
  MdChevronRight,
  MdAdd
} from "react-icons/md";
import { FileDown } from "lucide-react";
import OTBooking from './OTBooking.js';
import OTCalendar from './PatientFeedback.js';
import Inventory from './Inventory.js';
import User from './User.js';
import { API_BASE_URL } from '../lib/constants';
import { AuthContext } from '../contexts/AuthContext';
import IncompleteForms from './components/IncompleteForms';
import Dashboard from '../pages/components/Dashbord.js';
import DoctorDashboard from '../pages/components/DoctorDashbord.js';
import NurseDashboard from '../pages/components/NurseDashbord.js';
import RoleManagement from './RoleManagement.js';
import RoleList from './RoleList.js';
import AddRole from './AddRole.js';
import { useNotification } from '../contexts/NotificationContext';
import OTRooms from './OTRooms.js';

const MenuPage = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState('Dashboard');
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notificationsVisible, setNotificationsVisible] = useState(false);
  const [delayAlerts, setDelayAlerts] = useState([]);
  const [nurseNotifications, setNurseNotifications] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({ 'User Management': true, 'Operations': true });
  const [selectedRoleForEdit, setSelectedRoleForEdit] = useState(null);

  const { userRole, permissions } = useContext(AuthContext);

  const permissionMap = {
    'Dashboard': 'Dashboard View',
    'Patients': 'Patients View',
    'OT Rooms': 'OT Rooms View',
    'OT Booking': 'OT Booking View',
    'Patient Satisfaction': 'Patient Feedback View',
    'Inventory': 'Inventory View',
    'System User': 'Users View',
    'Role Management': 'Roles View',
    'Add Role': 'Roles Add'
  };

  const navGroups = [
    {
      label: 'Main',
      items: [
        { name: 'Dashboard', icon: MdDashboard }
      ]
    },
    {
      label: 'Clinical',
      items: [
        { name: 'Patients', icon: MdPeople },
        {
          name: 'Operations',
          icon: MdLocalHospital,
          subItems: [
            { name: 'OT Rooms', icon: MdMeetingRoom },
            { name: 'OT Booking', icon: MdEvent }
          ]
        },
        { name: 'Patient Satisfaction', icon: MdFeedback }
      ]
    },
    {
      label: 'Administration',
      items: [
        { name: 'Inventory', icon: MdInventory },
        {
          name: 'User Management',
          icon: MdGroup,
          subItems: [
            { name: 'System User', icon: MdPerson },
            { name: 'Role Management', icon: MdShield },
            { name: 'Add Role', icon: MdAdd }
          ]
        }
      ]
    }
  ];

  const allowedNavGroups = React.useMemo(() => {
    const filterByPermission = (item) => {
      const normalizedRole = (userRole || '').toLowerCase();
      if (normalizedRole === 'admin') return item;

      // Handle nested items first
      if (item.subItems) {
        const filteredSubItems = item.subItems.map(filterByPermission).filter(Boolean);

        // If it has children that are authorized, the parent reflects that
        if (filteredSubItems.length > 0) {
          return { ...item, subItems: filteredSubItems };
        }

        // If it has no authorized children, check if it has its own permission
        const permission = permissionMap[item.name];
        if (permission && permissions) {
          const hasPermission = Array.isArray(permission)
            ? permission.some(p => permissions.includes(p))
            : permissions.includes(permission);
          if (hasPermission) return item;
        }

        return null;
      }

      // Single item case
      const permission = permissionMap[item.name];
      if (!permission) return null; // Secure default: hide unmapped items

      const hasPermission = Array.isArray(permission)
        ? permission.some(p => permissions.includes(p))
        : permissions.includes(permission);

      return hasPermission ? item : null;
    };

    return navGroups.map(group => {
      const filteredItems = group.items.map(filterByPermission).filter(Boolean);
      return { ...group, items: filteredItems };
    }).filter(group => group.items.length > 0);
  }, [userRole, permissions]);

  const getDashboardComponent = () => {
    const role = (userRole || '').toLowerCase();
    if (role.includes('nurse')) return <NurseDashboard />;
    if (role.includes('doctor')) return <DoctorDashboard />;
    return <Dashboard />;
  };

  useEffect(() => {
    if (!permissions || permissions.length === 0) return;
    if (userRole === 'Admin') return;

    const hasDashboardView = permissions.includes('Dashboard View');
    if (activeMenu === 'Dashboard' && !hasDashboardView) {
      const allAllowedItems = allowedNavGroups.flatMap(g => g.items);
      const firstMenu = allAllowedItems[0];
      if (firstMenu) {
        if (firstMenu.subItems) {
          setActiveMenu(firstMenu.subItems[0].name);
        } else {
          setActiveMenu(firstMenu.name);
        }
      }
    }
  }, [permissions, userRole, allowedNavGroups]);

  useEffect(() => {
    if (location.state?.activeMenu) {
      setActiveMenu(location.state.activeMenu);
    }
  }, [location.state]);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1200);

      if (width < 768) {
        setSidebarHidden(true);
        setSidebarCollapsed(false);
      } else if (width >= 768 && width < 1200) {
        setSidebarHidden(true);
        setSidebarCollapsed(true);
      } else {
        setSidebarHidden(false);
        setSidebarCollapsed(false);
      }
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Fetch Delay Alerts (for non-nurse roles)
  useEffect(() => {
    if (userRole && userRole !== 'Nurse') {
      const fetchAlerts = () => {
        fetch(`${API_BASE_URL}/operation_delay_alerts`)
          .then(res => res.json())
          .then(data => {
            if (data.status === 'success') {
              setDelayAlerts(data.data || []);
            }
          })
          .catch(err => console.error('Error fetching delay alerts:', err));
      };
      fetchAlerts();
      const interval = setInterval(fetchAlerts, 30000);
      return () => clearInterval(interval);
    }
  }, [userRole]);

  // Fetch Nurse Notifications (for nurse role)
  useEffect(() => {
    if (userRole === 'Nurse') {
      const fetchNurseNotifications = () => {
        fetch(`${API_BASE_URL}/get_nurse_notifications`)
          .then(res => res.json())
          .then(data => {
            if (data.notifications) {
              const pending = data.notifications.filter(n => n.status === 'pending');
              setNurseNotifications(pending);
            }
          })
          .catch(err => console.error('Error fetching nurse notifications:', err));
      };
      fetchNurseNotifications();
      const interval = setInterval(fetchNurseNotifications, 60000);
      return () => clearInterval(interval);
    }
  }, [userRole]);

  const notificationRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const shownAlertsRef = useRef(new Set());

  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    if (match) return match[2];
  };

  const handleLogout = async () => {
    const csrfToken = getCookie("csrf_access_token");
    try {
      await fetch(`${API_BASE_URL}/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        credentials: 'include',
      });
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      navigate('/');
    }
  };

  const handleMenuClick = (menuName) => {
    setActiveMenu(menuName);
    if (isMobile || isTablet) setSidebarHidden(true);
  };

  const toggleSidebar = () => {
    if (isMobile || isTablet) setSidebarHidden(!sidebarHidden);
    else setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleSubMenu = (menuName) => {
    setExpandedMenus(prev => ({ ...prev, [menuName]: !prev[menuName] }));
  };

  const getNotificationCount = () => (userRole === 'Nurse' ? nurseNotifications.length : delayAlerts.length);
  const getNotificationTitle = () => (userRole === 'Nurse' ? "Pre-Op Orders" : "Delay Alerts");

  const renderNotifications = () => {
    if (userRole === 'Nurse') {
      return nurseNotifications.length === 0 ? (
        <div className="text-center text-gray-500 py-4 text-sm">No pre-operative orders pending.</div>
      ) : (
        nurseNotifications.map((n, idx) => (
          <div key={idx} className="border-l-4 p-3 mb-2 rounded text-sm bg-blue-50 border-blue-500 animate-fade-in">
            <div className="font-bold text-blue-900">{n.patient_name}</div>
            <div className="text-blue-700 text-xs">UHID: {n.uhid} | Room: {n.room}</div>
            <div className="mt-1 text-blue-800 font-medium">{n.proposed_surgery}</div>
          </div>
        ))
      );
    }
    return delayAlerts.length === 0 ? (
      <div className="text-center text-gray-500 py-4 text-sm">No delays reported.</div>
    ) : (
      delayAlerts.map((a, idx) => (
        <div key={idx} className="bg-red-50 border-l-4 border-red-500 p-3 mb-2 rounded text-sm animate-fade-in">
          <div className="font-bold text-red-900">{a.patient_name}</div>
          <div className="text-red-700">Delayed by {a.delay_minutes} mins</div>
        </div>
      ))
    );
  };

  const getMainMargin = () => (isMobile || isTablet ? '0' : sidebarCollapsed ? '80px' : '280px');

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-x-hidden">
      {/* Sidebar Mobile Overlay */}
      {(isMobile || isTablet) && !sidebarHidden && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[60] transition-opacity duration-300" onClick={() => setSidebarHidden(true)} />
      )}

      {/* SIDEBAR */}
      {/* Sidebar Sidebar - Professional & Sleek */}
      <aside
        className={`sidebar-container fixed top-0 left-0 h-full flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'w-20' : 'w-[280px]'
          } ${(isMobile || isTablet) ? (sidebarHidden ? '-translate-x-full' : 'translate-x-0') : 'translate-x-0'}`}
        style={{ zIndex: 100 }}
      >
        {/* Modern Logo Area */}
        <div className={`flex items-center space-x-3 ${sidebarCollapsed && !isMobile ? 'p-4 justify-center' : 'p-6'}`}>
          <div className="flex-shrink-0 w-11 h-11 bg-teal-600 rounded-xl flex items-center justify-center shadow-lg shadow-teal-100 logo-glow transition-transform hover:scale-105">
            <img src="/logo.png" alt="Logo" className="w-7 h-7 object-contain brightness-0 invert" />
          </div>
          {!sidebarCollapsed && (
            <div className="overflow-hidden whitespace-nowrap animate-slide-in">
              <h1 className="text-[22px] font-black text-[#1e293b] tracking-tight leading-none">SURGIX</h1>
              <p className="text-[10px] font-extrabold text-[#0d9488] uppercase tracking-[0.2em] mt-1">MEDICAL HUB</p>
            </div>
          )}
        </div>

        {/* Polished Navigation */}
        <nav className="flex-1 px-4 py-8 sidebar-scroll overflow-y-auto">
          {allowedNavGroups.map((group) => (
            <div key={group.label} className="mb-8 last:mb-0">
              {(!sidebarCollapsed || isMobile || isTablet) && (
                <p className="nav-group-label">
                  {group.label}
                </p>
              )}
              <ul className="space-y-1.5">
                {group.items.map((item) => {
                  const isActive = activeMenu === item.name || item.subItems?.some(s => s.name === activeMenu);
                  return (
                    <li key={item.name} className="sidebar-nav-item">
                      {item.subItems ? (
                        <>
                          <button
                            onClick={() => toggleSubMenu(item.name)}
                            className={`sidebar-link w-full ${sidebarCollapsed && !isMobile ? 'justify-center px-0' : ''} ${isActive ? 'active' : ''}`}
                          >
                            <item.icon className="sidebar-icon" />
                            {!sidebarCollapsed && (
                              <span className="flex-1 text-sm font-semibold text-left ml-3">{item.name}</span>
                            )}
                            {!sidebarCollapsed && (
                              <MdChevronRight className={`w-4 h-4 transition-transform duration-300 ${expandedMenus[item.name] ? 'rotate-90' : ''}`} />
                            )}
                          </button>
                          <div className={`submenu-container ${expandedMenus[item.name] && !sidebarCollapsed ? 'expanded' : ''}`}>
                            <ul className="submenu-list space-y-1">
                              {item.subItems.map(sub => (
                                <li key={sub.name}>
                                  <button
                                    onClick={() => handleMenuClick(sub.name)}
                                    className={`submenu-item w-full text-left ${activeMenu === sub.name ? 'active' : ''}`}
                                  >
                                    {sub.name}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </>
                      ) : (
                        <button
                          onClick={() => handleMenuClick(item.name)}
                          className={`sidebar-link w-full ${sidebarCollapsed && !isMobile ? 'justify-center px-0' : ''} ${activeMenu === item.name ? 'active' : ''}`}
                        >
                          <item.icon className="sidebar-icon mr-3" />
                          {!sidebarCollapsed && (
                            <span className="text-sm font-semibold">{item.name}</span>
                          )}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Enhanced User Profile Section */}
        <div className="p-4 border-t border-slate-50 mt-auto">
          {!sidebarCollapsed && (
            <div className="user-profile-card mb-3">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-xl avatar-badge text-white flex items-center justify-center font-bold text-base shadow-lg">
                  {userRole?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 overflow-hidden">
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">Signed Level</p>
                  <div className="flex items-center space-x-2">
                    <span className="text-[13px] font-bold text-slate-800 tracking-tight truncate">
                      {userRole}
                    </span>
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center ${sidebarCollapsed && !isMobile ? 'justify-center px-0' : 'space-x-3 px-4'} py-3.5 text-red-500 hover:bg-red-50 rounded-xl transition-all font-bold text-xs border border-transparent hover:border-red-100 uppercase tracking-widest`}
          >
            <MdLogout className="w-4 h-4 flex-shrink-0" />
            {!sidebarCollapsed && <span>Sign Out</span>}
          </button>
        </div>

      </aside>

      {/* REFINED MAIN CONTENT Area */}
      <main
        className="transition-all duration-300 min-h-screen flex flex-col bg-slate-50/50"
        style={{ marginLeft: getMainMargin() }}
      >
        <div className="p-4 md:p-8 flex-1">
          {/* Polished Glass Header */}
          <div className="flex items-center justify-between mb-8 glass-header p-5 rounded-3xl animate-fade-in border border-white/40">
            <div className="flex items-center space-x-6">
              {(isMobile || isTablet) && (
                <button onClick={toggleSidebar} className="p-3 bg-white hover:bg-teal-600 hover:text-white rounded-2xl transition-all shadow-sm border border-slate-100">
                  <MdMenu className="w-6 h-6" />
                </button>
              )}
              <div>
                <div className="flex items-center space-x-2 text-[10px] font-extrabold text-slate-400 uppercase tracking-[0.2em] mb-1 px-0.5">
                  <span className="hover:text-teal-600 cursor-pointer transition-colors">Workspace</span>
                  <MdChevronRight className="w-3.5 h-3.5" />
                  <span className="text-teal-700 font-black">{activeMenu}</span>
                </div>
                <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight">
                  {activeMenu}
                </h2>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {!isMobile && (
                <div className="relative group">
                  <i className='bx bx-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg group-focus-within:text-teal-600 transition-colors' />
                  <input
                    type="text"
                    placeholder="Search records..."
                    className="bg-slate-100/50 border-none rounded-2xl py-3 pl-12 pr-6 text-xs font-bold w-64 focus:ring-4 focus:ring-teal-500/10 focus:bg-white transition-all outline-none text-slate-800"
                  />
                </div>
              )}

              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => setNotificationsVisible(prev => !prev)}
                  className="p-3.5 bg-white border border-slate-100 rounded-2xl hover:border-teal-500 hover:shadow-lg hover:shadow-teal-100 transition-all text-slate-400 hover:text-teal-600 relative group"
                >
                  <MdNotifications className="w-6 h-6" />
                  {getNotificationCount() > 0 && (
                    <span className="absolute top-3 right-3 w-3 h-3 bg-red-500 border-2 border-white rounded-full animate-bounce" />
                  )}
                </button>

                {notificationsVisible && (
                  <div className="absolute top-full right-0 mt-4 glass-panel rounded-3xl shadow-2xl z-50 overflow-hidden w-80 border border-slate-100 animate-fade-in origin-top-right">
                    <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/30">
                      <h3 className="font-black text-slate-800 text-xs uppercase tracking-widest">{getNotificationTitle()}</h3>
                      <button onClick={() => setNotificationsVisible(false)} className="text-slate-400 hover:text-red-500"><MdClose className="w-5 h-5" /></button>
                    </div>
                    <div className="p-2 max-h-[400px] overflow-y-auto">
                      {renderNotifications()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Dynamic Content Sections */}
          <div className="mt-4 flex-1">
            {activeMenu === 'Dashboard' && <IncompleteForms />}

            <div className="content-wrapper">
              {activeMenu === 'Dashboard' && (
                (userRole || '').toLowerCase() === 'admin' ? <Dashboard /> : (permissions.includes('Dashboard View') ? getDashboardComponent() : <div className="p-10 text-center font-bold text-slate-400">Access Denied</div>)
              )}
              {activeMenu === 'Patients' && <PatientListTable />}
              {activeMenu === 'OT Rooms' && <OTRooms />}
              {activeMenu === 'OT Booking' && <OTBooking />}
              {activeMenu === 'Patient Satisfaction' && <OTCalendar />}
              {activeMenu === 'Inventory' && <Inventory />}
              {(activeMenu === 'System User') && <User />}

              {activeMenu === 'Add Role' && (
                <RoleList
                  onEditRole={(role) => { setSelectedRoleForEdit(role); setActiveMenu('Edit Role'); }}
                  onAddRole={() => setActiveMenu('Role Management')}
                />
              )}
              {activeMenu === 'Role Management' && (
                <AddRole onSuccess={() => setActiveMenu('Add Role')} onCancel={() => setActiveMenu('Add Role')} />
              )}
              {activeMenu === 'Edit Role' && (
                <RoleManagement
                  role={selectedRoleForEdit}
                  onSuccess={() => { setSelectedRoleForEdit(null); setActiveMenu('Add Role'); }}
                  onCancel={() => { setSelectedRoleForEdit(null); setActiveMenu('Add Role'); }}
                />
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MenuPage;